import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PlusIcon,
  XMarkIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import adminReferralService, {
  AdminReferral,
} from '../api/services/referral.service';

/* =========================================================
   PAGE
========================================================= */

const Referrals: React.FC = () => {
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tier, setTier] = useState<'all' | 'user' | 'agent'>('all');

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<AdminReferral | null>(null);
  const [confirmDemote, setConfirmDemote] = useState<AdminReferral | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, tier]);

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2600);
  };

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['referrals'] });
  };

  const statsQuery = useQuery({
    queryKey: ['referrals', 'stats'],
    queryFn: () => adminReferralService.stats(),
  });

  const listQuery = useQuery({
    queryKey: ['referrals', 'list', page, limit, debouncedSearch, tier],
    queryFn: () =>
      adminReferralService.list({
        page,
        limit,
        search: debouncedSearch,
        tier,
      }),
  });

  const referrals = listQuery.data?.referrals ?? [];
  const pagination = listQuery.data?.pagination;

  const handleToggle = async (r: AdminReferral) => {
    setBusy(true);
    try {
      await adminReferralService.toggleEnabled(r.id);
      showToast('success', r.enabled ? 'Disabled' : 'Enabled');
      refreshAll();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDemote = async () => {
    if (!confirmDemote) return;
    setBusy(true);
    try {
      await adminReferralService.demote(confirmDemote.id);
      showToast('success', 'Demoted to user');
      setConfirmDemote(null);
      refreshAll();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referrals</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage user referrals and agent partners.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={refreshAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Create Agent
          </Button>
        </div>
      </div>

      {/* Stats */}
      {statsQuery.data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Stat label="Total Codes" value={statsQuery.data.total} />
          <Stat label="Agents" value={statsQuery.data.agents} tone="purple" />
          <Stat label="Users" value={statsQuery.data.users} tone="green" />
          <Stat label="Total Signups" value={statsQuery.data.totalSignups} />
          <Stat
            label="Total Paid"
            value={`ETB ${statsQuery.data.totalPaid.toLocaleString()}`}
          />
        </div>
      )}

      {/* Filters */}
      <Card padding="sm" className="mb-4">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by code or telegram id..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
          </div>

          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as any)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2"
          >
            <option value="all">All</option>
            <option value="user">Users only</option>
            <option value="agent">Agents only</option>
          </select>
        </div>
      </Card>

      {/* Table */}
      <Card padding="sm" className="!p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Owner</th>
                <th className="px-4 py-3 text-left font-semibold">Code</th>
                <th className="px-4 py-3 text-center font-semibold">Tier</th>
                <th className="px-4 py-3 text-right font-semibold">Per Signup</th>
                <th className="px-4 py-3 text-right font-semibold">Signups</th>
                <th className="px-4 py-3 text-right font-semibold">Paid</th>
                <th className="px-4 py-3 text-center font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listQuery.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : referrals.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    No referrals found
                  </td>
                </tr>
              ) : (
                referrals.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{r.displayName}</p>
                      <p className="text-[10px] text-gray-500 font-mono">
                        {r.telegramId}
                      </p>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-800">
                      {r.code}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.isAgent ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700">
                          Agent
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                      {r.rewardAmount != null
                        ? `ETB ${r.rewardAmount.toLocaleString()}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      {r.referredCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                      ETB {r.totalRevenue.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.enabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setEditOpen(r)}
                          className="px-2 py-1 rounded text-[10px] font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                          title="Edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggle(r)}
                          disabled={busy}
                          className="px-2 py-1 rounded text-[10px] font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                        >
                          {r.enabled ? 'Disable' : 'Enable'}
                        </button>
                        {r.isAgent && (
                          <button
                            onClick={() => setConfirmDemote(r)}
                            disabled={busy}
                            className="px-2 py-1 rounded text-[10px] font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                            title="Demote to user"
                          >
                            <UserMinusIcon className="w-3.5 h-3.5 inline" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.pages} · {pagination.total}{' '}
              total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Add agent dialog */}
      {addOpen && (
        <AddAgentDialog
          onClose={() => setAddOpen(false)}
          onDone={(msg, ok) => {
            showToast(ok ? 'success' : 'error', msg);
            if (ok) setAddOpen(false);
            refreshAll();
          }}
        />
      )}

      {/* Edit dialog */}
      {editOpen && (
        <EditReferralDialog
          referral={editOpen}
          onClose={() => setEditOpen(null)}
          onDone={(msg, ok) => {
            showToast(ok ? 'success' : 'error', msg);
            if (ok) setEditOpen(null);
            refreshAll();
          }}
        />
      )}

      {/* Demote confirm */}
      {confirmDemote && (
        <ConfirmDialog
          title={`Demote "${confirmDemote.displayName}"?`}
          description="This removes agent status and clears the custom reward. The referral code stays valid as a normal user code."
          confirmLabel={busy ? 'Demoting...' : 'Demote'}
          danger
          onCancel={() => setConfirmDemote(null)}
          onConfirm={handleDemote}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

/* =========================================================
   SMALL COMPONENTS
========================================================= */

const Stat: React.FC<{
  label: string;
  value: number | string;
  tone?: 'purple' | 'green';
}> = ({ label, value, tone }) => (
  <Card>
    <p className="text-[10px] uppercase text-gray-500 tracking-wide font-semibold">
      {label}
    </p>
    <p
      className={`text-lg font-bold mt-1 ${
        tone === 'purple'
          ? 'text-purple-700'
          : tone === 'green'
          ? 'text-green-700'
          : 'text-gray-900'
      }`}
    >
      {value}
    </p>
  </Card>
);

const Modal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50" onClick={onClose} />
    <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
          <XMarkIcon className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const ConfirmDialog: React.FC<{
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, description, confirmLabel, danger, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
    <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
      <h3 className="text-base font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 text-sm rounded-lg text-white ${
            danger
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const AddAgentDialog: React.FC<{
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => void;
}> = ({ onClose, onDone }) => {
  const [telegramId, setTelegramId] = useState('');
  const [reward, setReward] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!telegramId.trim()) {
      onDone('Telegram ID is required', false);
      return;
    }
    setBusy(true);
    try {
      await adminReferralService.createAgent({
        telegramId: telegramId.trim(),
        rewardAmount: reward.trim() === '' ? null : Number(reward),
      });
      onDone('Agent created', true);
    } catch (e: any) {
      onDone(e?.response?.data?.message || e?.message || 'Failed', false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Create Agent" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Telegram ID
          </label>
          <input
            type="text"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
            placeholder="e.g. 123456789"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
          <p className="text-[10px] text-gray-500 mt-1">
            The user must already exist in the system. Their referral code
            will be marked as agent tier.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reward per signup (ETB)
          </label>
          <input
            type="number"
            min={0}
            value={reward}
            onChange={(e) => setReward(e.target.value)}
            placeholder="Leave empty to use default"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          Cancel
        </button>
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
};

const EditReferralDialog: React.FC<{
  referral: AdminReferral;
  onClose: () => void;
  onDone: (msg: string, ok: boolean) => void;
}> = ({ referral, onClose, onDone }) => {
  const [reward, setReward] = useState(
    referral.rewardAmount != null ? String(referral.rewardAmount) : ''
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await adminReferralService.update(referral.id, {
        rewardAmount: reward.trim() === '' ? null : Number(reward),
      });
      onDone('Saved', true);
    } catch (e: any) {
      onDone(e?.response?.data?.message || e?.message || 'Failed', false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Edit ${referral.displayName}`} onClose={onClose}>
      <div className="space-y-3">
        <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 text-xs">
          <p>
            <span className="text-gray-500">Code:</span>{' '}
            <span className="font-mono text-gray-900">{referral.code}</span>
          </p>
          <p className="mt-1">
            <span className="text-gray-500">Tier:</span>{' '}
            <span className="font-medium text-gray-900">
              {referral.isAgent ? 'Agent' : 'User'}
            </span>
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Reward per signup (ETB)
          </label>
          <input
            type="number"
            min={0}
            value={reward}
            onChange={(e) => setReward(e.target.value)}
            placeholder="Leave empty to use default"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
        >
          Cancel
        </button>
        <Button size="sm" onClick={submit} disabled={busy}>
          {busy ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
};

export default Referrals;